import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, Table, Modal, Form, Input, Popconfirm, Space, Tag, App, Checkbox, Grid, Tabs, InputNumber, Switch, TimePicker, Select } from "antd";
import { useTranslation } from "react-i18next";
import axios from "../../services/axios";
// import MainBtn from "../../components/main/main_btn/MainBtn";
import dayjs from "dayjs";
import AuthContext from "../../contexts/AuthContext";
import {
  getDefaultNumericParameterErrors,
  getDefaultParameterDisplayUnit,
  getDefaultParameterNumericBounds,
} from "../../lib/deviceDefaultParameterConstraints";
import { sortConfigsByNumber } from "../../lib/parameterConfiguration";

type DefaultParameter = {
  field_name: string;
  value: any;
};

type ParameterConfiguration = {
  id: number;
  name: string;
  commissioning_tip_en?: string;
  commissioning_tip_fr?: string;
  commissioning_tip_de?: string;
  commissioning_tip_nl?: string;
  config_number?: number | null;
  advanced_device_parameters: string[];
  not_applicable_device_parameters?: string[];
  default_parameters?: DefaultParameter[];
  available_device_parameters?: string[];
};

const getFieldLabelKey = (paramName: string): string | null => {
  const labelKeyMap: Record<string, string> = {
    mode: "device_settings_field_mode",
    running_state: "device_settings_field_running_state",
    installation_type: "device_settings_field_installation_type",
    valve_position: "device_settings_field_valve_position",
    water_heater_min_power: "device_settings_field_water_heater_min_power",
    water_heater_max_power: "device_settings_field_water_heater_max_power",
    boost_percentage: "device_settings_field_boost_percentage",
    meter_factor: "device_settings_field_meter_factor",
    circulator_deadtime_min: "device_settings_field_circulator_deadtime_min",
    circulator_power_percent: "device_settings_field_circulator_power",
    valve_temperature_limit: "device_settings_field_valve_temperature_limit",
    heater_max_temp: "device_settings_field_heater_max_temp",
    duration_temperature_after_circulation_min: "device_settings_field_duration_temperature_after_circulation",
    sanitation_max_period_days: "device_settings_field_sanitation_max_period",
    sanitation_active: "device_settings_field_sanitation_active",
    sanitation_hour_start_time: "device_settings_field_sanitation_hour_start_time",
    sanitation_temperature: "device_settings_field_sanitation_temperature",
    t_cold: "device_settings_field_t_cold",
    t_lukewarm: "device_settings_field_t_lukewarm",
    t_hot: "device_settings_field_t_hot",
    t_very_hot: "device_settings_field_t_very_hot",
    t_max: "device_settings_field_t_max",
    use_filtered_temperature: "device_settings_field_use_filtered_temperature",
    firmware_automatic_update: "device_settings_field_firmware_automatic_update",
    max_installation_power: "device_settings_field_max_installation_power",
    handle_dropout: "device_settings_field_handle_dropout",
    installation_phase: "device_settings_field_installation_phase",
    dropout_period_start_time: "device_settings_field_dropout_period_start",
    dropout_period_end_time: "device_settings_field_dropout_period_end",
    dropout_t_max: "device_settings_field_dropout_t_max",
    dropout_v_max: "device_settings_field_dropout_v_max",
    dropout_v_threshold: "device_settings_field_dropout_v_threshold",
    dropout_power: "device_settings_field_dropout_power",
    dropout_v_max_l2: "device_settings_field_dropout_v_max_l2",
    dropout_v_threshold_l2: "device_settings_field_dropout_v_threshold_l2",
    dropout_power_l2: "device_settings_field_dropout_power_l2",
    dropout_v_max_l3: "device_settings_field_dropout_v_max_l3",
    dropout_v_threshold_l3: "device_settings_field_dropout_v_threshold_l3",
    dropout_power_l3: "device_settings_field_dropout_power_l3",
  };
  
  return labelKeyMap[paramName] || null;
};

const getFieldChoices = (
  paramName: string,
  t: (key: string) => string,
  configName?: string
): Array<{ key: string; label: string }> | null => {
  const configNumber = configName
    ? parseInt(configName.match(/Config\s+(\d+)/i)?.[1] ?? "0", 10)
    : 0;

  const valvePositionChoices =
    [2, 32, 62].includes(configNumber)
      ? [
          { key: "HYBRID", labelKey: "device_settings_field_valve_position_hybrid" },
          { key: "FULL_SOLAR", labelKey: "device_settings_field_valve_position_full_solar" },
        ]
      : [8, 38, 68].includes(configNumber)
        ? [
            { key: "WATER_HEATER", labelKey: "device_settings_field_valve_position_water_heater_mode" },
            { key: "HEATER", labelKey: "device_settings_field_valve_position_heater_mode" },
          ]
        : [
            { key: "WATER_HEATER", labelKey: "device_settings_field_valve_position_water_heater_mode" },
            { key: "HEATER", labelKey: "device_settings_field_valve_position_heater_mode" },
            { key: "FULL_SOLAR", labelKey: "device_settings_field_valve_position_full_solar" },
            { key: "HYBRID", labelKey: "device_settings_field_valve_position_hybrid" },
          ];

  const choicesMap: Record<string, Array<{ key: string; labelKey: string }>> = {
    mode: [
      { key: "SOLO", labelKey: "device_settings_field_mode_solo" },
      { key: "DUO", labelKey: "device_settings_field_mode_duo" },
    ],
    running_state: [
      { key: "BOOST", labelKey: "device_settings_field_running_state_boost" },
      { key: "NORMAL", labelKey: "device_settings_field_running_state_normal" },
      { key: "SLEEP", labelKey: "device_settings_field_running_state_sleep" },
    ],
    installation_type: [
      { key: "WATER_HEATER_MODE", labelKey: "device_settings_field_installation_type_water_heater_mode" },
      { key: "HEATER_MODE", labelKey: "device_settings_field_installation_type_heater_mode" },
    ],
    valve_position: valvePositionChoices,
  };

  const choices = choicesMap[paramName];
  if (!choices) return null;

  return choices.map((choice) => ({
    key: choice.key,
    label: choice.labelKey.includes("device_settings_") ? t(choice.labelKey) : choice.labelKey,
  }));
};

const isSanitizationEnabledInDefaults = (defaultParams: Record<string, any>): boolean => {
  const v = defaultParams.sanitation_active;
  return v === true || v === 1 || v === "true" || v === "True";
};

const SANITATION_PARAM_ORDER = [
  "sanitation_active",
  "sanitation_max_period_days",
  "sanitation_hour_start_time",
  "sanitation_temperature",
] as const;

const SANITATION_PARAM_SET = new Set<string>(SANITATION_PARAM_ORDER);

const mergeParamsWithSanitationGroup = (
  paramNames: string[],
  defaultParams: Record<string, any>
): string[] => {
  const available = new Set(paramNames);
  const on = isSanitizationEnabledInDefaults(defaultParams);
  const block: string[] = [];
  if (available.has("sanitation_active")) {
    block.push("sanitation_active");
  }
  if (on) {
    for (const k of [
      "sanitation_max_period_days",
      "sanitation_hour_start_time",
      "sanitation_temperature",
    ] as const) {
      if (available.has(k)) {
        block.push(k);
      }
    }
  }
  if (block.length === 0) {
    return paramNames;
  }
  const result: string[] = [];
  let inserted = false;
  for (const paramName of paramNames) {
    if (SANITATION_PARAM_SET.has(paramName)) {
      if (!inserted) {
        result.push(...block);
        inserted = true;
      }
      continue;
    }
    result.push(paramName);
  }
  if (!inserted) {
    result.push(...block);
  }
  return result;
};

const isHandleDropoutEnabledInDefaults = (defaultParams: Record<string, any>): boolean => {
  const v = defaultParams.handle_dropout;
  return v === true || v === 1 || v === "true" || v === "True";
};

const DROPOUT_PARAM_ORDER = [
  "handle_dropout",
  "installation_phase",
  "dropout_period_start_time",
  "dropout_t_max",
  "dropout_v_max",
  "dropout_v_threshold",
  "dropout_power",
  "dropout_v_max_l2",
  "dropout_v_threshold_l2",
  "dropout_power_l2",
  "dropout_v_max_l3",
  "dropout_v_threshold_l3",
  "dropout_power_l3",
  "dropout_period_end_time",
] as const;

const DROPOUT_PARAM_SET = new Set<string>(DROPOUT_PARAM_ORDER);

const DROPOUT_HIDABLE_WHEN_OFF = new Set<string>([
  "installation_phase",
  "dropout_period_start_time",
  "dropout_period_end_time",
  "dropout_t_max",
  "dropout_v_max",
  "dropout_v_threshold",
  "dropout_power",
  "dropout_v_max_l2",
  "dropout_v_threshold_l2",
  "dropout_power_l2",
  "dropout_v_max_l3",
  "dropout_v_threshold_l3",
  "dropout_power_l3",
]);

const mergeParamsWithDropoutGroup = (
  paramNames: string[],
  defaultParams: Record<string, any>
): string[] => {
  const available = new Set(paramNames);
  const on = isHandleDropoutEnabledInDefaults(defaultParams);
  const block: string[] = [];
  for (const k of DROPOUT_PARAM_ORDER) {
    if (!available.has(k)) {
      continue;
    }
    if (k === "handle_dropout") {
      block.push(k);
      continue;
    }
    if (on) {
      block.push(k);
    }
  }
  if (block.length === 0) {
    return paramNames;
  }

  const withoutDropout = paramNames.filter((n) => !DROPOUT_PARAM_SET.has(n));
  let anchor = paramNames.indexOf("handle_dropout");
  if (anchor < 0) {
    anchor = paramNames.findIndex((n) => DROPOUT_PARAM_SET.has(n));
  }
  if (anchor < 0) {
    return [...withoutDropout, ...block];
  }
  const nonDropoutBeforeAnchor = paramNames
    .slice(0, anchor)
    .filter((n) => !DROPOUT_PARAM_SET.has(n)).length;

  return [
    ...withoutDropout.slice(0, nonDropoutBeforeAnchor),
    ...block,
    ...withoutDropout.slice(nonDropoutBeforeAnchor),
  ];
};

const getParameterType = (paramName: string, t?: (key: string) => string): 'number' | 'boolean' | 'time' | 'string' | 'select' => {
  const hasChoices = ['mode', 'running_state', 'installation_type', 'valve_position'].includes(paramName);
  if (hasChoices) {
    return 'select';
  }
  if ([
    'firmware_automatic_update',
    'staging',
    'disable_email_notifications',
    'use_filtered_temperature',
  ].includes(paramName)) {
    return 'boolean';
  }

  if (paramName.includes('active') || paramName.includes('enable') || paramName.includes('handle') || paramName.includes('correct')) {
    return 'boolean';
  }
  
  if (paramName === 'circulator_deadtime_min' || 
      paramName === 'duration_temperature_after_circulation_min' ||
      paramName === 'sanitation_hour_start_time') {
    return 'number';
  }
  
  const timeFields = [
    'dropout_period_start_time',
    'dropout_period_end_time',
    'notification_quiet_start_time',
    'notification_quiet_end_time',
  ];
  if (timeFields.includes(paramName)) {
    return 'time';
  }
  
  if (paramName.startsWith('t_') || paramName.includes('power') || paramName.includes('period') || 
      paramName.includes('temperature') || paramName.includes('factor') || paramName.includes('percentage') ||
      paramName.includes('phase') || paramName.includes('hour') || paramName.includes('min') || 
      paramName.includes('max') || paramName.includes('v_') || paramName.includes('_v') ||
      paramName === 'priority') {
    return 'number';
  }
  
  return 'string';
};

const DefaultParametersInput: React.FC<{
  availableParams: string[];
  value?: DefaultParameter[];
  onChange?: (value: DefaultParameter[]) => void;
  configName?: string;
  fieldErrors?: Partial<Record<string, string>>;
  onClearFieldError?: (fieldName: string) => void;
}> = ({ availableParams, value, onChange, configName, fieldErrors, onClearFieldError }) => {
  const { t } = useTranslation("settings");
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const list = Array.isArray(value) ? value : [];
  const defaultParams = useMemo(() => {
    const dict: Record<string, any> = {};
    list.forEach((item: DefaultParameter) => {
      if (item?.field_name) {
        dict[item.field_name] = item.value;
      }
    });
    return dict;
  }, [list]);

  const visibleParamNames = useMemo(() => {
    const sanitationOn = isSanitizationEnabledInDefaults(defaultParams);
    const dropoutOn = isHandleDropoutEnabledInDefaults(defaultParams);
    const filtered = availableParams.filter((paramName) => {
      if (
        (paramName === "sanitation_max_period_days" ||
          paramName === "sanitation_hour_start_time" ||
          paramName === "sanitation_temperature") &&
        !sanitationOn
      ) {
        return false;
      }
      if (DROPOUT_HIDABLE_WHEN_OFF.has(paramName) && !dropoutOn) {
        return false;
      }
      return true;
    });
    return mergeParamsWithDropoutGroup(
      mergeParamsWithSanitationGroup(filtered, defaultParams),
      defaultParams
    );
  }, [availableParams, defaultParams]);

  const updateParam = (paramName: string, val: any) => {
    onClearFieldError?.(paramName);
    const updatedList = list.filter((item: DefaultParameter) => item?.field_name !== paramName);
    if (val !== null && val !== undefined && val !== '') {
      updatedList.push({ field_name: paramName, value: val });
    }
    onChange?.(updatedList);
  };

  const removeParam = (paramName: string) => {
    const updatedList = list.filter((item: DefaultParameter) => item?.field_name !== paramName);
    onChange?.(updatedList);
  };

  return (
    <div style={isMobile ? undefined : { maxHeight: '400px', overflowY: 'auto' }}>
      <div style={{ marginBottom: 16, color: '#666', fontSize: isMobile ? '11px' : '12px' }}>
        {t("default_parameters_help", "Set default values for parameters. Leave empty to not set a default.")}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 16 : 12 }}>
        {visibleParamNames.map((paramName) => {
          const paramType = getParameterType(paramName);
          const currentValue = defaultParams[paramName];
          const numericBounds = getDefaultParameterNumericBounds(paramName, configName);
          const displayUnit = getDefaultParameterDisplayUnit(paramName);

          return (
            <div 
              key={paramName} 
              style={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 8 : 8, 
                alignItems: isMobile ? 'stretch' : 'center', 
                padding: isMobile ? '12px' : '8px', 
                border: '1px solid #d9d9d9', 
                borderRadius: '4px' 
              }}
            >
              <div style={{ 
                flex: isMobile ? 'none' : '0 0 200px', 
                fontWeight: 500,
                fontSize: isMobile ? '13px' : '14px',
                marginBottom: isMobile ? 4 : 0
              }}>
                {getFieldLabelKey(paramName) ? t(getFieldLabelKey(paramName)!) : paramName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </div>
              <div
                style={{
                  flex: isMobile ? "none" : 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                  width: isMobile ? "100%" : "auto",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  {paramType === "boolean" ? (
                    <Switch
                      checked={currentValue === true || currentValue === "true" || currentValue === 1}
                      onChange={(checked) => updateParam(paramName, checked)}
                      size={isMobile ? "small" : "default"}
                    />
                  ) : paramType === "time" ? (
                    <TimePicker
                      value={currentValue ? dayjs(currentValue, "HH:mm:ss") : null}
                      onChange={(time) => updateParam(paramName, time ? time.format("HH:mm:ss") : null)}
                      format="HH:mm:ss"
                      style={{ width: "100%" }}
                      size={isMobile ? "middle" : "large"}
                    />
                  ) : paramType === "select" ? (
                    <Select
                      value={currentValue || undefined}
                      onChange={(value) => updateParam(paramName, value)}
                      placeholder={t("no_default", "No default")}
                      allowClear
                      style={{ width: "100%", flex: 1 }}
                      size={isMobile ? "middle" : "large"}
                      status={fieldErrors?.[paramName] ? "error" : undefined}
                    >
                      {getFieldChoices(paramName, t, configName)?.map((choice) => (
                        <Select.Option key={choice.key} value={choice.key}>
                          {choice.label}
                        </Select.Option>
                      ))}
                    </Select>
                  ) : paramType === "number" ? (
                    <>
                      <InputNumber
                        value={currentValue}
                        onChange={(value) => updateParam(paramName, value)}
                        style={{ width: "100%", flex: 1 }}
                        placeholder={t("no_default", "No default")}
                        size={isMobile ? "middle" : "large"}
                        status={fieldErrors?.[paramName] ? "error" : undefined}
                        min={numericBounds?.min}
                        max={numericBounds?.max}
                        step={numericBounds?.step}
                      />
                      {displayUnit ? (
                        <span
                          style={{
                            color: "#012124",
                            opacity: 0.45,
                            fontSize: isMobile ? 13 : 14,
                            flexShrink: 0,
                          }}
                        >
                          {displayUnit}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <Input
                      value={currentValue}
                      onChange={(e) => updateParam(paramName, e.target.value)}
                      placeholder={t("no_default", "No default")}
                      style={{ flex: 1 }}
                      size={isMobile ? "middle" : "large"}
                      status={fieldErrors?.[paramName] ? "error" : undefined}
                    />
                  )}
                  {currentValue !== undefined && currentValue !== null && currentValue !== "" && (
                    <Button
                      size={isMobile ? "middle" : "small"}
                      type="text"
                      danger
                      onClick={() => {
                        onClearFieldError?.(paramName);
                        removeParam(paramName);
                      }}
                      style={{ flexShrink: 0 }}
                    >
                      {t("clear", "Clear")}
                    </Button>
                  )}
                </div>
                {fieldErrors?.[paramName] ? (
                  <span style={{ color: "#ff4d4f", fontSize: 12 }}>{fieldErrors[paramName]}</span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DeviceParametersManagement: React.FC = () => {
  const { t, i18n } = useTranslation("settings");
  const { message } = App.useApp();
  const { user } = useContext(AuthContext);
  const screens = Grid.useBreakpoint();
  const isAdmin = user?.is_staff || user?.is_superuser;
  const isMobile = !screens.md;
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ParameterConfiguration[]>([]);
  const [editing, setEditing] = useState<ParameterConfiguration | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm<ParameterConfiguration>();
  const [activeTab, setActiveTab] = useState("basic");
  const [defaultParamFieldErrors, setDefaultParamFieldErrors] = useState<Partial<Record<string, string>>>(
    {}
  );

  // Commented: New configuration from FE - if user creates from FE, others would see it without default values.
  // Prefer creation only in BE by admin so all users see the config with proper defaults.
  // const nextSuggestedName = useMemo(() => {
  //   const regex = /^Config\s+(\d+)(?:\s*-.*)?$/i;
  //   const nums = (items || [])
  //     .map((it) => {
  //       const m = regex.exec(it.name || "");
  //       return m ? parseInt(m[1], 10) : null;
  //     })
  //     .filter((n): n is number => typeof n === "number");
  //   const max = nums.length ? Math.max(...nums) : 0;
  //   return `Config ${max + 1}`;
  // }, [items]);

  const fetchAll = async () => {
    setLoading(true);
    await axios
      .get("/api-v1/config/parameter-configurations/")
      .then((resp) => setItems(sortConfigsByNumber(resp.data || [])))
      .catch(() => message.error(t("error_load_configurations")));
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    i18n.loadNamespaces("settings");
  }, [i18n]);

  // const openCreate = () => {
  //   setEditing(null);
  //   form.resetFields();
  //   form.setFieldsValue({
  //     name: nextSuggestedName,
  //     advanced_device_parameters: [],
  //     default_parameters: [],
  //   });
  //   setActiveTab("basic");
  //   setModalOpen(true);
  // };

  const openEdit = (item: ParameterConfiguration) => {
    setEditing(item);
    setDefaultParamFieldErrors({});
    form.setFieldsValue({ 
      ...item, 
      default_parameters: item.default_parameters || [] 
    });
    setActiveTab("basic");
    setModalOpen(true);
  };

  // Commented: delete from FE - removal only in BE by admin to avoid accidental deletion of shared configs.
  // const handleDelete = async (id: number) => {
  //   await axios
  //     .delete(`/api-v1/config/parameter-configurations/${id}/`)
  //     .then(() => {
  //       message.success(t("success_configuration_deleted"));
  //       fetchAll();
  //     })
  //     .catch(() => message.error(t("error_delete_configuration")));
  // };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const numericErrors = getDefaultNumericParameterErrors(
        values.default_parameters,
        t,
        values.name || editing?.name
      );
      if (Object.keys(numericErrors).length > 0) {
        setDefaultParamFieldErrors(numericErrors);
        return;
      }
      setDefaultParamFieldErrors({});
      const payload: Partial<ParameterConfiguration> = {
        name: values.name,
        advanced_device_parameters: values.advanced_device_parameters || [],
        default_parameters: values.default_parameters || [],
      };
      if (editing) {
        await axios
          .patch(`/api-v1/config/parameter-configurations/${editing.id}/`, payload)
          .then(() => {
            message.success(t("success_configuration_updated"));
            closeModal();
            fetchAll();
          })
          .catch((error: any) => {
            handleServerError(error);
          });
      } else {
        // Commented: create from FE - creation only in BE by admin so all users see configs with proper defaults
        // await axios
        //   .post(`/api-v1/config/parameter-configurations/`, payload)
        //   .then(() => {
        //     message.success(t("success_configuration_created"));
        //     setModalOpen(false);
        //     fetchAll();
        //   })
        //   .catch((error: any) => {
        //     handleServerError(error);
        //   });
      }
    } catch {
      // Ant Design form validation failed (e.g. name pattern)
    }
  };

  const [resetLoading, setResetLoading] = useState(false);
  const handleResetToAdminDefaults = async () => {
    if (!editing?.id) return;
    setResetLoading(true);
    try {
      const resp = await axios.post(
        `/api-v1/config/parameter-configurations/${editing.id}/reset-to-admin-defaults/`
      );
      if (resp.data) {
        message.success(t("success_reset_to_admin_defaults", "Defaults reset to admin values."));
        closeModal();
        fetchAll();
      }
    } catch {
      message.error(t("error_reset_to_admin_defaults", "Failed to reset to admin defaults."));
    } finally {
      setResetLoading(false);
    }
  };

  const handleServerError = (error: any) => {
    if (error?.response?.data) {
      const errorData = error.response.data;
      
      if (errorData.name) {
        const nameErrors = Array.isArray(errorData.name) ? errorData.name : [errorData.name];
        form.setFields([
          {
            name: 'name',
            errors: nameErrors,
          },
        ]);
      } else if (errorData.default_parameters) {
        const dpErrors = Array.isArray(errorData.default_parameters)
          ? errorData.default_parameters
          : [errorData.default_parameters];
        const msg = dpErrors.map(String).join(" ");
        setDefaultParamFieldErrors({ circulator_power_percent: msg });
      } else {
        const errorMessage = 
          errorData.detail || 
          errorData.message || 
          errorData.non_field_errors?.[0] || 
          t("error_save_configuration");
        message.error(errorMessage);
      }
    } else {
      message.error(t("error_network_or_server"));
    }
  };

  const currentAvailableParams = useMemo(() => {
    if (editing && editing.available_device_parameters) return editing.available_device_parameters;
    if (items.length && items[0].available_device_parameters) return items[0].available_device_parameters;
    return [] as string[];
  }, [editing, items]);

  const defaultParamsExcluded = useMemo(() => [
    'name',
    'parameter_configuration',
    'device_id',
    'own_address_ip',
    'circulator_state',
    'valve_state',
    'valve_feedback',
    'voltage_soliseco',
    'schedule',
  ], []);

  const currentDefaultParams = useMemo(() => {
    return currentAvailableParams.filter(param => !defaultParamsExcluded.includes(param));
  }, [currentAvailableParams, defaultParamsExcluded]);

  const clearDefaultParamFieldError = (fieldName: string) => {
    setDefaultParamFieldErrors((prev) => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const closeModal = () => {
    setModalOpen(false);
    setDefaultParamFieldErrors({});
  };

  return (
    <div style={{ padding: isMobile ? 12 : 24 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "space-between", marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? 18 : 22 }}>{t("configurations_management_title")}</h2>
        {/* Commented: New configuration from FE - creation only in BE by admin so all users see configs with proper defaults */}
        {/* <MainBtn
          title={t("new_configuration")}
          onClick={openCreate}
          color="primary"
          variant="outline"
        /> */}
      </div>
      <Table
        rowKey="id"
        loading={loading}
        dataSource={items}
        scroll={{ x: true }}
        pagination={{ pageSize: isMobile ? 5 : 10, showSizeChanger: !isMobile }}
        columns={[
          { title: t("table_name_header"), dataIndex: "name", ellipsis: true },
          {
            title: t("table_advanced_parameters_header"),
            dataIndex: "advanced_device_parameters",
            render: (vals: string[]) => (
              <Space wrap>
                {(vals || []).map((k) => (
                  <Tag key={k}>{k}</Tag>
                ))}
              </Space>
            ),
          },
          {
            title: t("table_actions_header"),
            render: (_: any, rec: ParameterConfiguration) => (
              <Space wrap>
                <Button size={isMobile ? "small" : "middle"} onClick={() => openEdit(rec)}>{t("edit_button")}</Button>
                {/* Commented: delete from FE - removal only in BE by admin to avoid accidental deletion of shared configs */}
                {/* <Popconfirm title={t("delete_configuration_confirm")} onConfirm={() => handleDelete(rec.id)}>
                  <Button size={isMobile ? "small" : "middle"} danger>{t("delete_button")}</Button>
                </Popconfirm> */}
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title={editing ? t("edit_configuration", {name: editing.name}) : t("new_configuration_modal")}
        open={modalOpen}
        onOk={onSubmit}
        onCancel={closeModal}
        okText={editing ? t("save_configuration") : t("create_configuration")}
        width={isMobile ? "calc(100vw - 16px)" : 800}
        centered={!isMobile}
        style={isMobile ? {
          top: 8,
          marginBottom: 8,
          height: "calc(100vh - 16px)",
          maxHeight: "calc(100vh - 16px)",
          maxWidth: "calc(100vw - 16px)",
        } : undefined}
        styles={isMobile ? {
          content: { height: "100%", display: "flex", flexDirection: "column", minHeight: 0 },
          header: { flexShrink: 0 },
          body: {
            padding: 12,
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
          },
        } : undefined}
        footer={isMobile ? null : (
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {editing?.id && !isAdmin && (
                <Popconfirm
                  title={t("settings:reset_to_admin_defaults_confirm", "Are you sure you want to reset to admin defaults?")}
                  onConfirm={handleResetToAdminDefaults}
                  okText={t("settings:confirm", "Confirm")}
                  cancelText={t("settings:cancel", "Cancel")}
                >
                  <Button loading={resetLoading}>
                    {t("settings:reset_to_admin_defaults", "Reset to Admin Defaults")}
                  </Button>
                </Popconfirm>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button onClick={closeModal}>{t("cancel")}</Button>
              <Button type="primary" onClick={onSubmit}>
                {editing ? t("save_configuration") : t("create_configuration")}
              </Button>
            </div>
          </div>
        )}
      >
        <Form form={form} layout="vertical">
          <Tabs 
            activeKey={activeTab} 
            onChange={setActiveTab}
            items={[
              {
                key: "basic",
                label: t("basic_settings", "Basic Settings"),
                children: (
                  <>
                    <Form.Item
                      name="name"
                      label={t("configuration_name_label")}
                      rules={[
                        { required: true },
                        {
                          pattern: /^Config\s+\d+(?:\s*-.*)?$/,
                          message: t("configuration_name_pattern_error"),
                        },
                      ]}
                    >
                      <Input size={isMobile ? "middle" : "large"} />
                    </Form.Item>
                    <Form.Item name="advanced_device_parameters" label={t("advanced_parameters_label")}>
                      <Checkbox.Group
                        options={(currentAvailableParams || []).map((k) => ({ label: k, value: k }))}
                        style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
                      />
                    </Form.Item>
                  </>
                ),
              },
              {
                key: "defaults",
                label: t("default_values", "Default Values"),
                forceRender: true,
                children: (
                  <Form.Item
                    name="default_parameters"
                    label={t("default_parameters_label", "Default Device Parameters")}
                    tooltip={t("default_parameters_tooltip", "Set default values for device parameters. These will be applied when a device changes to this configuration (if user chooses to use defaults). Changes made here or in Django Admin will sync bidirectionally.")}
                  >
                    <DefaultParametersInput
                      availableParams={currentDefaultParams}
                      configName={editing?.name}
                      fieldErrors={defaultParamFieldErrors}
                      onClearFieldError={clearDefaultParamFieldError}
                    />
                  </Form.Item>
                ),
              },
            ]}
          />
          {isMobile && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
              {editing?.id && !isAdmin && (
                <Popconfirm
                  title={t("settings:reset_to_admin_defaults_confirm", "Are you sure you want to reset to admin defaults?")}
                  onConfirm={handleResetToAdminDefaults}
                  okText={t("settings:confirm", "Confirm")}
                  cancelText={t("settings:cancel", "Cancel")}
                >
                  <Button loading={resetLoading} block>
                    {t("settings:reset_to_admin_defaults", "Reset to Admin Defaults")}
                  </Button>
                </Popconfirm>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <Button onClick={closeModal} style={{ flex: 1 }}>{t("cancel")}</Button>
                <Button type="primary" onClick={onSubmit} style={{ flex: 1 }}>
                  {editing ? t("save_configuration") : t("create_configuration")}
                </Button>
              </div>
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default DeviceParametersManagement;


